const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Escape'])

interface ScrollFlightOptions {
  duration?: number
  onProgress: (progress: number, easedProgress: number) => void
  onComplete: () => void
  onCancel: () => void
}

/** One clock drives scrolling and the decorative flight; user input always wins. */
export function scrollToTopWithFlight({ duration = 1600, onProgress, onComplete, onCancel }: ScrollFlightOptions) {
  const startY = window.scrollY
  const startedAt = performance.now()
  const style = document.documentElement.style
  const previousBehavior = style.getPropertyValue('scroll-behavior')
  const previousPriority = style.getPropertyPriority('scroll-behavior')
  let frame = 0
  let stopped = false

  // Each RAF tick sets the exact position, rather than starting a second native animation.
  style.setProperty('scroll-behavior', 'auto', 'important')

  function cleanup() {
    cancelAnimationFrame(frame)
    window.removeEventListener('wheel', cancel)
    window.removeEventListener('touchstart', cancel)
    window.removeEventListener('pointerdown', cancel)
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('resize', cancel)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    if (previousBehavior) style.setProperty('scroll-behavior', previousBehavior, previousPriority)
    else style.removeProperty('scroll-behavior')
  }

  function cancel() {
    if (stopped) return
    stopped = true
    cleanup()
    onCancel()
  }

  function onKeyDown(event: KeyboardEvent) {
    if (SCROLL_KEYS.has(event.key)) cancel()
  }

  function onVisibilityChange() {
    if (document.hidden) cancel()
  }

  function tick(now: number) {
    if (stopped) return
    const progress = Math.min(1, Math.max(0, (now - startedAt) / duration))
    const eased = progress * progress * (3 - 2 * progress)
    window.scrollTo({ top: startY * (1 - eased), behavior: 'auto' })
    onProgress(progress, eased)
    if (progress === 1) {
      stopped = true
      cleanup()
      onComplete()
    } else {
      frame = requestAnimationFrame(tick)
    }
  }

  window.addEventListener('wheel', cancel, { passive: true })
  window.addEventListener('touchstart', cancel, { passive: true })
  window.addEventListener('pointerdown', cancel, { passive: true })
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', cancel)
  document.addEventListener('visibilitychange', onVisibilityChange)
  onProgress(0, 0)
  frame = requestAnimationFrame(tick)
  return cancel
}
